import { create } from 'zustand'
import { useAccountMatterStore } from './use-account-matter-store';
import { KnowledgeLibrary } from '@/types/knowledge-library';
import { useFeatureFlagsStore } from './use-feature-flags-store';

export type SelectedKnowledgeOption = 'auto-select' | 'select' | 'none'

type MatterAvailableKnowledgeSourcesStore = {
  knowledgeLibraries: KnowledgeLibrary[] | undefined
  isLoading: boolean
  errorMessage: string
  selectedKnowledgeOption: SelectedKnowledgeOption
  setSelectedKnowledgeOption: (option: SelectedKnowledgeOption) => void
  refetchKnowledgeLibraries: () => void
}

export const useMatterAvailableKnowledgeSourcesStore = create<MatterAvailableKnowledgeSourcesStore>((set, _get) => ({
  knowledgeLibraries: undefined,
  isLoading: true,
  errorMessage: '',
  selectedKnowledgeOption: 'auto-select',
  setSelectedKnowledgeOption: (selectedKnowledgeOption) => {
    set({ selectedKnowledgeOption })
  },
  refetchKnowledgeLibraries: () => {
    // Call the existing fetchData function
    void fetchData().catch((err) => {
      console.error('Error refetching available knowledge sources:', err);
    });
  }
}));

const fetchData = async () => {
  const { matter } = useAccountMatterStore.getState();


  if (!matter || !useFeatureFlagsStore.getState()['archie-kl-retrieval']) {
    useMatterAvailableKnowledgeSourcesStore.setState({ isLoading: false, errorMessage: '' });
    return;
  }

  useMatterAvailableKnowledgeSourcesStore.setState({ isLoading: true, errorMessage: '' });

  try {
    const response = await fetch(`/api/knowledge-library`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ matterTypeIds: matter.matterTypeId ? [matter.matterTypeId] : [] })
    })
    if (!response.ok) {
      throw new Error(`Something went wrong trying to get knowledge libraries`)
    }
    const knowledgeLibraries = (await response.json()) as KnowledgeLibrary[]
    useMatterAvailableKnowledgeSourcesStore.setState({ knowledgeLibraries });
  } catch (err) {
    console.warn('Error fetching available knowledge sources in store:', err);
    if (err instanceof Error) {
      useMatterAvailableKnowledgeSourcesStore.setState({ errorMessage: err.message });
    }
  } finally {
    useMatterAvailableKnowledgeSourcesStore.setState({ isLoading: false });
  }
}

const subscribeToMatterTypeIdAndKLRetrievalFF = () => {
  const unsubscribe = useAccountMatterStore.subscribe(
    (state, prevState) => {
      if (state.matter?.matterTypeId !== prevState.matter?.matterTypeId) {
        void fetchData().catch((err) => {
          console.error('Error fetching available knowledge sources in store:', err);
        });
      }
    }
  );

  const unsubscribe2 = useFeatureFlagsStore.subscribe(
    (state, prevState) => {
      if (state['archie-kl-retrieval'] !== prevState['archie-kl-retrieval']) {
        void fetchData().catch((err) => {
          console.error('Error fetching available knowledge sources in store:', err);
        });
      }
    }
  );

  // Return cleanup function
  return () => {
    // Unsubscribe from the store
    unsubscribe();
    unsubscribe2();
  };
}


export const initMatterAvailableKnowledgeSourcesStore = () => {
  const disableAccountSub = subscribeToMatterTypeIdAndKLRetrievalFF();
  void fetchData();

  return [disableAccountSub];
}
